/**
 * Request a password-reset link. Always shows the same confirmation
 * regardless of outcome — the backend itself never reveals whether the
 * email is registered either (no user-enumeration).
 */

import { useState, type FormEvent } from 'react'
import { Link } from 'react-router'
import { api } from '../api'
import { LangSwitch } from '../components/LangSwitch'
import { PivotLogo } from '../components/PivotLogo'
import { ThemeToggle } from '../components/ThemeToggle'
import { useI18n } from '../i18n'
import '../styles/auth-forms.css'
import '../styles/header-actions.css'

export default function ForgotPassword() {
  const { t } = useI18n()
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    try {
      await api.auth.forgotPassword(email)
    } finally {
      setSent(true)
      setSubmitting(false)
    }
  }

  return (
    <div className="auth-page">
      <title>{`${t('auth.forgotTitle')} — ${t('common.appName')}`}</title>
      <header className="auth-bar">
        <Link to="/" className="auth-brand" aria-label={t('a11y.authHomeLink')}>
          <PivotLogo />
          {t('common.appName')}
        </Link>
        <div className="header-actions">
          <LangSwitch />
          <ThemeToggle />
        </div>
      </header>
      <div className="auth-body">
        <div className="auth-card">
          <h1>{t('auth.forgotTitle')}</h1>
          {sent ? (
            <p className="form-success">{t('auth.forgotSent')}</p>
          ) : (
            <>
              <p>{t('auth.forgotBody')}</p>
              <form onSubmit={handleSubmit} className="stack auth-form">
                <label>
                  {t('auth.emailLabel')}
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                    autoComplete="email"
                  />
                </label>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {t('auth.forgotCta')}
                </button>
              </form>
            </>
          )}
          <p className="auth-links">
            <Link to="/login" className="auth-link">
              {t('auth.backToLogin')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
